import { EuiInputPopover, EuiSelectable } from "@inscopix/ideas-eui";
import { useState } from "react";
import { FormikErrors } from "formik";
import { PersonalSpaceFormFields } from "../FormCreatePersonalSpace";
import { StateCode, US_STATES } from "../constants/us-states";
import { CANADA_PROVINCES, ProvinceCode } from "../constants/canada-provinces";

interface StateProvinceSelectableProps {
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  country: "US" | "CA";
  value: StateCode | ProvinceCode | undefined;
  setFieldValue: (
    field: string,
    value: React.SetStateAction<StateCode | ProvinceCode>,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<PersonalSpaceFormFields>>;
}

export const StateProvinceSelectable = ({
  value,
  country,
  handleBlur,
  setFieldValue,
}: StateProvinceSelectableProps) => {
  const isUSA = country === "US";
  const [isOpen, setIsOpen] = useState(false);
  const OPTIONS: ReadonlyArray<{
    CODE: StateCode | ProvinceCode;
    NAME: string;
  }> = isUSA ? US_STATES : CANADA_PROVINCES;
  const name = OPTIONS.find((stateOrProvince) => stateOrProvince.CODE === value)
    ?.NAME;
  const [inputValue, setInputValue] = useState<string>(name ?? "");
  const onChange = async (changedOption: {
    value: StateCode | ProvinceCode;
  }) => {
    await setFieldValue("state", changedOption.value);
  };

  return (
    <EuiSelectable
      aria-label={isUSA ? "Select state" : "Select province"}
      options={[
        ...OPTIONS.map((option) => ({
          value: option.CODE,
          label: option.NAME,
          searchableLabel: `${option.NAME} ${option.CODE}`,
          checked: option.CODE === value ? ("on" as const) : undefined,
        })),
      ]}
      onChange={(newOptions, event, changedOption) => {
        if (changedOption.checked === "on") {
          setInputValue(changedOption.label);
          void onChange(changedOption);
        }
        setIsOpen(false);
      }}
      singleSelection
      searchable
      searchProps={{
        value: inputValue,
        onChange: (value) => {
          setInputValue(value);
        },
        onBlur: handleBlur,
        placeholder: `Please select your ${isUSA ? "state" : "province"}`,
        onKeyDown: (event) => {
          if (event.key === "Tab") return setIsOpen(false);
          if (event.key !== "Escape") return setIsOpen(true);
        },
        onClick: () => setIsOpen(true),
        onFocus: () => setIsOpen(true),
      }}
      listProps={{
        style: { maxHeight: 400, overflow: "scroll" },
        isVirtualized: true,
      }}
    >
      {(list, search) => (
        <EuiInputPopover
          autoFocus
          fullWidth
          closePopover={() => {
            setIsOpen(false);
            setInputValue(name ?? "");
          }}
          disableFocusTrap
          closeOnScroll
          isOpen={isOpen}
          input={search ?? ""}
          panelPaddingSize="none"
        >
          {list}
        </EuiInputPopover>
      )}
    </EuiSelectable>
  );
};
